import { SXStyleSheet } from '@/utils';

/**
 * Rahmen-Bausteine (Nacharbeit Beratungsstrecke, 27.10.2026,
 * docs/tasks/design-rollout.md): EINE Quelle fuer Navy-Rahmen, Kopfband,
 * Rundecke, Logo, Sidebar und ihre Eintraege. CRM-Shell (views/home) und
 * Beratungsstrecke (BeratungScrollView) benutzen dieselben Bausteine, damit
 * sie nicht wieder auseinanderlaufen — die Strecke hatte eine eigene Kopie
 * der Sidebar, die beim Rollout nicht mitgezogen wurde (eingeklappt 64px mit
 * 16px Innenrand + 12px Eintragsrand: die Icons wurden zerdrueckt).
 *
 * Die Werte sind unveraendert aus SideBar/style.ts, SideBarItem/style.ts,
 * AppBar.tsx und views/home/index.tsx hierher gezogen.
 */

// Korrekturrunde 1 (Alex 26.10.2026): eingeklappt 64 statt 82 — Icon 20px plus
// je 22px Rand (8px Sidebar-Innenabstand + zentrierter Eintrag 48px).
export const SIDEBAR_WIDTH_COLLAPSED = 64;
export const SIDEBAR_WIDTH_EXPANDED = 216;
// Korrekturrunde 2 (Alex 26.10.2026): wieder 48px wie release/bwlive.
export const APP_BAR_HEIGHT = 48;

// Feste Hoehe des Brand-Lockups (Logo + 2-zeiliger Firmenname). In BEIDEN
// States identisch reserviert, damit die Nav-Liste immer bei derselben
// Y-Position startet — sonst springt sie beim Toggle um ~34px, weil der
// Firmenname nur im expanded-State gerendert wird. Wert = gemessene
// expanded-Hoehe (Logo 38px + 5px Gap + 2 Zeilen Name). Im collapsed-State
// bleibt der Platz unter dem Logo bewusst leer.
export const BRAND_LOCKUP_HEIGHT = 74;

/** Radius der Rundecke zwischen Navy-Rahmen und Inhalt. */
const RUNDECKE = 16;

const style = SXStyleSheet.create({
  navyRahmen: {
    bgcolor: 'background.navigation',
  },
  // Kopfband ueber dem Inhalt. Stufe 2b: Navy wie Original.
  band: {
    flexDirection: 'row',
    alignItems: 'center',
    bgcolor: 'background.navigation',
    minHeight: APP_BAR_HEIGHT,
    height: APP_BAR_HEIGHT,
    flexShrink: 0,
  },
  // Rundecke zwischen Navy-Rahmen und Inhalt wie im Original 2272451fa (16px
  // oben links, Korrekturrunde 1, Alex 26.10.2026). Die Ecke stanzt in die
  // Rahmenfarbe aus — sie setzt also voraus, dass darueber und links davon
  // `background.navigation` liegt.
  inhaltsflaeche: (theme) => ({
    borderRadius: `${RUNDECKE}px 0 0 0`,
    bgcolor: 'background.screen',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      width: RUNDECKE,
      height: RUNDECKE,
      background: `radial-gradient(circle at bottom right, transparent ${RUNDECKE}px, ${theme.palette.background.navigation} ${RUNDECKE + 1}px)`,
      pointerEvents: 'none',
      zIndex: 1,
    },
  }),
  sidebar: (theme) => ({
    position: 'relative',
    height: '100%',
    justifyContent: 'space-between',
    // Stufe 2b (25.10.2026): Navy wie Original, Aufbau aus Stufe 2.
    borderRight: '1px solid',
    borderColor: 'navigation.rand',
    background: theme.palette.background.navigation,
    alignItems: 'flex-start',
    // Korrekturrunde 3: ein Innenabstand fuer BEIDE Zustaende (8px) — vorher 16px
    // ausgeklappt und 8px eingeklappt, die Icons sprangen um 6,5px.
    px: 2,
    pt: 2.5,
    pb: 8,
    transition: 'width 150ms cubic-bezier(0.4, 0, 0.2, 1)',
    overflow: 'visible',
    flexShrink: 0,
  }),
  sidebarEingeklappt: {
    width: `${SIDEBAR_WIDTH_COLLAPSED}px`,
  },
  sidebarAusgeklappt: {
    width: `${SIDEBAR_WIDTH_EXPANDED}px`,
  },
  // Korrekturrunde 4 (Alex 26.10.2026): Logo steht beim Umschalten still —
  // Zeile statt zentrierter Spalte, Logo-Mitte auf der Icon-Achse (x 32:
  // 8px Sidebar-Rand + 8px + 16px halbe Logobreite), Firmenname rechts
  // daneben. Vorher sprang das Logo von x 12,5 (zu) auf 88,5 (auf).
  // Abschluss Rollout: Logo 38 -> 32px, damit der Name (12px, unveraendert)
  // in ZWEI Zeilen passt: „Deutsche Investitions-" / „und Foerderberatung AG"
  // braucht 144px, die Zeile bietet 216 - 16 - 8 - 32 - 12 = 148px.
  brandLockup: {
    width: '100%',
    height: `${BRAND_LOCKUP_HEIGHT}px`,
    flexShrink: 0,
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 3,
    overflow: 'hidden',
  },
  logo: {
    height: 32,
    width: 'auto',
    ml: '8px',
    flexShrink: 0,
  },
  // Feste Breite: waehrend die Sidebar waechst, wird der Name abgeschnitten
  // statt neu umzubrechen; er blendet in 150ms ein wie die Beschriftungen.
  companyName: (theme) => ({
    ...theme.typography.label,
    color: 'navigation.text',
    textAlign: 'left',
    width: 148,
    flexShrink: 0,
    animation: 'sideBarFirmaEin 150ms ease-out',
    '@keyframes sideBarFirmaEin': {
      from: { opacity: 0 },
      to: { opacity: 1 },
    },
    '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
  }),
  toggleButton: {
    alignSelf: 'flex-end',
    minWidth: 'auto',
    marginTop: '8px',
    padding: '4px',
    color: 'navigation.text',
    cursor: 'pointer',
    borderRadius: '8px',
    transition: 'transform 150ms ease, background-color 150ms ease',
    '&:hover': {
      backgroundColor: 'background.navigationAktiv',
    },
  },
  toggleButtonCollapsed: {
    alignSelf: 'center',
  },
  toggleIconExpanded: {
    transform: 'rotate(180deg)',
  },
  backButton: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    // Icon an derselben Stelle wie die Eintraege (Korrekturrunde 3): 14px Rand.
    gap: 3,
    padding: '8px 14px',
    borderRadius: '8px',
    cursor: 'pointer',
    width: '100%',
    justifyContent: 'flex-start',
    overflow: 'hidden',
    '&:hover': {
      backgroundColor: 'background.navigationAktiv',
    },
  },
  // Schrift per variant am Element.
  backButtonLabel: {
    color: 'navigation.text',
    whiteSpace: 'nowrap',
  },
});

/**
 * Sidebar-Eintrag. Design-Test Stufe 2 (25.10.2026), Farben Stufe 2b: Navy
 * wie Original.
 *
 * Eintrag: Text 14/500 weiss, Icon 20px (Icons erben `currentColor`).
 * Aktiv: Flaeche `background.navigationAktiv`, Text und Icon Gold
 * (`navigation.aktiv` = Original `secondary.main`) — keine Balken.
 * Alle Werte aus dem Theme.
 */
export const eintragStyle = SXStyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    height: '44px',
    borderRadius: '8px',
    transition: 'background-color 150ms ease',
    '&:hover': {
      backgroundColor: 'background.navigationAktiv',
    },
  },
  containerAltColor: {
    '&:hover': {
      backgroundColor: 'background.navigationAktiv',
    },
  },
  containerActive: {
    color: 'navigation.aktiv',
    backgroundColor: 'background.navigationAktiv',
  },
  buttonContainerActive: {
    color: 'navigation.aktiv',
  },
  altColorActive: {
    backgroundColor: 'background.navigationAktiv',
    '&:hover': {
      backgroundColor: 'background.navigationAktiv',
    },
  },
  buttonContainer: {
    flex: 1,
    height: '100%',
    display: 'flex',
    flexDirection: 'row',
    // Korrekturrunde 1: 12px Luecke + 12px Rand wie im Original — mit 16/16 blieben
    // bei 216px Sidebar nur 116px Text, „Antragserstellung" lief ins Ellipsis.
    gap: 3,
    alignItems: 'center',
    cursor: 'pointer',
    justifyContent: 'flex-start',
    color: 'navigation.text',
    // Korrekturrunde 3: 14px Rand in BEIDEN Zustaenden, Icon immer links —
    // 8px Sidebar + 14px = Icon bei x 22, eingeklappt 22 + 20 + 22 = 64px.
    // Kein Zentrieren im eingeklappten Zustand mehr: das liess die Icons beim
    // Umschalten springen (gemessen 21,5 / 28 / kurz 84px).
    px: '14px',
  },
  // Schrift per `variant="wertBetont"` am Element — an einer Typography
  // ueberstimmt `sx.typography` die Aktiv-Farbe.
  labelText: {
    // Beschriftung blendet beim Aufklappen ein, waehrend die Breite waechst
    // (Korrekturrunde 3); abgeschnitten wird per Ellipsis, nie umgebrochen.
    animation: 'sideBarBeschriftungEin 150ms ease-out',
    '@keyframes sideBarBeschriftungEin': {
      from: { opacity: 0 },
      to: { opacity: 1 },
    },
    '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
    // Zeilenhoehe 24 wie im Original; die Rolle bringt 20.
    lineHeight: '24px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    flex: 1,
    textAlign: 'left',
    color: 'navigation.text',
  },
  labelTextActive: {
    color: 'navigation.aktiv',
  },
  chevronButton: {
    marginLeft: 'auto',
    minWidth: 0,
    padding: '4px',
    borderRadius: '8px',
  },
  chevronIcon: {
    color: 'navigation.text',
    transition: 'transform 150ms ease',
  },
  chevronIconExpanded: {
    transform: 'rotate(180deg)',
  },
  subItemsContainer: {
    pl: 8,
  },
  subItemButton: {
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
    height: '36px',
    typography: 'meta',
    color: 'navigation.text',
    borderRadius: '8px',
    pl: 4,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    transition: 'background-color 150ms ease',
    '&:hover': {
      backgroundColor: 'background.navigationAktiv',
      color: 'navigation.aktiv',
    },
  },
  subItemActive: {
    typography: 'wertBetont',
    color: 'navigation.aktiv',
    backgroundColor: 'background.navigationAktiv',
  },
});

export default style;
